import React, { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, } from "@mui/material";
import { Add } from "@mui/icons-material";
import * as XLSX from 'xlsx';
import { Autocomplete } from "@mui/material";

const initialForm = {
  image: "",
  code: "",
  name: "",
  weight: "",
  // quantity: "",
  type: "",
  subType: "",
};

const goldColor = "#BFA96B";
const taupeColor = "#A89F91";
const darkText = "#3C3B3F";
const bgColor = "#F4F1EE";
const buttonBg = "#BFA96B";
const buttonHover = "#A1893E";

const Products = () => {
  const { user } = useAuth();
  const role = user?.role;
  const [productTypes, setProductTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);

  const [showProductModal, setShowProductModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [editingTypeId, setEditingTypeId] = useState(null);

  const [subTypes, setSubTypes] = useState([]);
  const [selectedSubType, setSelectedSubType] = useState(null);

  const [cartQuantities, setCartQuantities] = useState({});
  const [cartWeights, setCartWeights] = useState({});
  const [cartSizes, setCartSizes] = useState({});
  const [cartColor, setCartColor] = useState({});
  const [cartRemark, setCartRemark] = useState({});

  const [cart, setCart] = useState([]);
  const [showCartModal, setShowCartModal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, onConfirm: null, message: '' });

  const [searchCode, setSearchCode] = useState("");
  const [unitMode, setUnitMode] = useState({});

  const [parentOrderId, setParentOrderId] = useState('');
  const [selectedCartItems, setSelectedCartItems] = useState({});
  const [selectAllCart, setSelectAllCart] = useState(false);


  useEffect(() => {
    loadProductTypes();
  }, []);

  const loadProductTypes = async () => {
    try {
      const types = await api.getProductTypes();
      const normalized = types.map((t, index) =>
        typeof t === "string" ? { id: index + 1, name: t } : t
      );
      setProductTypes(normalized);
    } catch (err) {
      console.error("Failed to fetch product types", err.message);
    }
  };

  const handleSearchByCode = async () => {
    if (!searchCode.trim()) return;
    try {
      const all = await api.getAllProducts();
      const found = all.find((p) => p.code.toLowerCase() === searchCode.toLowerCase());
      if (!found) return alert("❌ Product not found.");

      const type = productTypes.find((t) => t.name === found.type);
      if (!type) return alert("❌ Type not found");
      setSelectedType(type);
      await loadSubTypes(type.name);

      if (found.subType) {
        setSelectedSubType(found.subType);
        const filtered = await api.getProductsByTypeAndSubType(type.name, found.subType);
        setProducts(filtered);
      } else {
        setProducts(await api.getProductsByType(type.name));
      }
    } catch (err) {
      console.error("Search failed", err.message);
    }
  };

  const loadProducts = async (typeName) => {
    try {
      const items = await api.getProductsByType(typeName);
      setProducts(items);
      setCartQuantities({});
    } catch (err) {
      console.error("Failed to fetch products", err.message);
    }
  };

  const loadSubTypes = async (typeName) => {
    try {
      const data = await api.getSubTypesByType(typeName);
      setSubTypes(data);
    } catch (err) {
      console.error("Failed to fetch subtypes", err.message);
    }
  };

  const handleTypeSelect = async (type) => {
    setSelectedType(type);
    setSelectedSubType(null);
    await loadSubTypes(type.name);
    await loadProducts(type.name);
  };

  const toggleUnitMode = (productId) => {
    setUnitMode((prev) => ({
      ...prev,
      [productId]: prev[productId] === 'pieces' ? 'weight' : 'pieces',
    }));
  };

  const handleSubTypeSelect = async (subType) => {
    setSelectedSubType(subType);
    try {
      const filtered = await api.getProductsByTypeAndSubType(selectedType.name, subType);
      setProducts(filtered);
      setCartQuantities({});
    } catch (err) {
      console.error("Failed to fetch subtype products", err.message);
    }
  };

  const handleProductSubmit = async () => {
    if (!form.type) return alert("Please select product type.");
    if (!form.subType) return alert("Please select product sub-type.");

    const formData = new FormData();
    if (form.image instanceof File) {
      formData.append('image', form.image);
    } else if (typeof form.image === 'string') {
      // If image is URL (when editing), no need to send new image file
    }
    formData.append('code', form.code);
    formData.append('name', form.name);
    formData.append('weight', form.weight || '');
    // formData.append('quantity', form.quantity);
    formData.append('type', form.type);
    formData.append('subType', form.subType);

    try {
      if (editingId) {
        await api.updateProduct(editingId, formData);
      } else {
        await api.addProduct(formData);
      }

      closeProductModal();
      loadProducts(form.type);
    } catch (err) {
      console.error("Product save failed:", err.message);
    }
  };

  const openProductModal = (product = null) => {
    if (product) {
      setForm({
        image: product.image,
        code: product.code,
        name: product.name,
        weight: product.weight,
        // quantity: product.quantity || 0,
        type: product.type || "",
        subType: product.subType || "",
      });
      setEditingId(product.id);
      loadSubTypes(product.type || "");
    } else {
      setForm({
        ...initialForm,
        type: selectedType?.name || "",
      });
      setEditingId(null);
      if (selectedType?.name) loadSubTypes(selectedType.name);
    }

    setShowProductModal(true);
  };

  const closeProductModal = () => {
    setShowProductModal(false);
    setForm(initialForm);
    setEditingId(null);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await api.deleteProduct(id);
      loadProducts(selectedType.name);
    } catch (err) {
      console.error("Failed to delete product", err.message);
    }
  };

  const closeTypeModal = () => {
    setShowTypeModal(false);
    setNewTypeName("");
    setEditingTypeId(null);
  };

  const handleSaveType = async () => {
    if (!newTypeName.trim()) return;

    try {
      if (editingTypeId) {
        await fetch(`/api/product-types/${editingTypeId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: newTypeName }),
        });
      } else {
        await fetch(`/api/product-types`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: newTypeName }),
        });
      }

      closeTypeModal();
      loadProductTypes();
    } catch (err) {
      console.error("Type save failed", err.message);
    }
  };

  const handleAddVariation = (productId) => {
    const newQuantities = { ...cartQuantities };
    if (!newQuantities[productId]) newQuantities[productId] = [];
    newQuantities[productId].push(1);
    setCartQuantities(newQuantities);

    const newWeights = { ...cartWeights };
    if (!newWeights[productId]) newWeights[productId] = [];
    newWeights[productId].push('');
    setCartWeights(newWeights);

    const newSizes = { ...cartSizes };
    if (!newSizes[productId]) newSizes[productId] = [];
    newSizes[productId].push('');
    setCartSizes(newSizes);

    const newColor = { ...cartColor };
    if (!newColor[productId]) newColor[productId] = [];
    newColor[productId].push('Yellow');
    setCartColor(newColor);

    const newRemark = { ...cartRemark };
    if (!newRemark[productId]) newRemark[productId] = [];
    newRemark[productId].push('-');
    setCartRemark(newRemark);
  };

  const handleRemoveVariation = (productId, index) => {
    const newQuantities = { ...cartQuantities };
    if (newQuantities[productId]) {
      newQuantities[productId].splice(index, 1);
      setCartQuantities(newQuantities);
    }

    const newWeights = { ...cartWeights };
    if (newWeights[productId]) {
      newWeights[productId].splice(index, 1);
      setCartWeights(newWeights);
    }

    const newSizes = { ...cartSizes };
    if (newSizes[productId]) {
      newSizes[productId].splice(index, 1);
      setCartSizes(newSizes);
    }

    const newColor = { ...cartColor };
    if (newColor[productId]) {
      newColor[productId].splice(index, 1);
      setCartColor(newColor);
    }

    const newRemark = { ...cartRemark };
    if (newRemark[productId]) {
      newRemark[productId].splice(index, 1);
      setCartRemark(newRemark);
    }
  };

  const handleAddToCart = async (product) => {
    try {
      const orders = await api.getOrders();

      const pendingOrders = orders.filter(
        order =>
          order.productCode === product.code &&
          order.status.toLowerCase() === 'pending'
      );

      const variations = [];
      const quantities = cartQuantities[product.id] || [1];
      const weights = cartWeights[product.id] || [''];
      const sizes = cartSizes[product.id] || [''];
      const color = cartColor[product.id] || [''];
      const remark = cartRemark[product.id] || [''];

      const unit = unitMode[product.id] || 'weight';
      for (let i = 0; i < quantities.length; i++) {
        let weightLabel = weights[i] || '';
        if (unit === 'pieces' && weightLabel) {
          weightLabel = `${weightLabel} (Pieces)`;
        }
        variations.push({
          cartId: `${product.id}-${weightLabel || ''}-${sizes[i] || ''}-${Date.now()}`, // stable unique ID
          product,
          quantity: quantities[i] || 1,
          weight: weightLabel || '',
          size: sizes[i] || '',
          color: color[i] || 'Yellow',
          remark: remark[i] || '-'
        });
      }

      const addToCart = () => {
        setCart(prev => {
          const updatedCart = [...prev];
          variations.forEach(variation => {
            const existingIndex = prev.findIndex(
              item =>
                item.product.id === variation.product.id &&
                item.weight === variation.weight &&
                item.size === variation.size &&
                item.color === variation.color &&
                item.remark === variation.remark
            );
            if (existingIndex !== -1) {
              updatedCart[existingIndex].quantity += variation.quantity;
            } else {
              updatedCart.push(variation);
            }
          });
          return updatedCart;
        });

        // Clear variations only if no warning or after confirm
        if (pendingOrders.length === 0) {
          setCartQuantities(prev => ({ ...prev, [product.id]: [] }));
          setCartWeights(prev => ({ ...prev, [product.id]: [] }));
          setCartSizes(prev => ({ ...prev, [product.id]: [] }));
          setCartColor(prev => ({ ...prev, [product.id]: [] }));
          setCartRemark(prev => ({ ...prev, [product.id]: [] }));
        }

        setConfirmDialog({ open: false, onConfirm: null, message: '' });
      };

      if (pendingOrders.length > 0) {
        // const totalPendingQty = pendingOrders.reduce(
        //   (sum, order) => sum + Number(order.quantity),
        //   0
        // );

        setConfirmDialog({
          open: true,
          message: `⚠️ You already have ${pendingOrders.length} pending order(s) for "${product.name}" (Code: ${product.code}) with the following details:\n\n` +
            pendingOrders.map(order =>
              `• Weight: ${order.weight || 'N/A'}, Size: ${order.size || 'N/A'}, Color: ${order.color || 'N/A'}`
            ).join('\n') +
            `\n\n Do you want to continue adding this item to the cart?`,
          onConfirm: () => {
            addToCart();
            // Clear variations only after user confirms
            setCartQuantities(prev => ({ ...prev, [product.id]: [] }));
            setCartWeights(prev => ({ ...prev, [product.id]: [] }));
            setCartSizes(prev => ({ ...prev, [product.id]: [] }));
            setCartColor(prev => ({ ...prev, [product.id]: [] }));
            setCartRemark(prev => ({ ...prev, [product.id]: [] }));
          },
        });
        return;
      }

      addToCart();
    } catch (err) {
      console.error('Error checking pending orders:', err.message);
      alert('❌ Could not verify pending orders. Try again.');
    }
  };

  const handleRemoveSelectedItems = () => {
    setCart(prevCart =>
      prevCart.filter(item => !selectedCartItems[item.cartId])
    );
    setSelectedCartItems({});
  };

  const handleSelectAllCart = (checked) => {
    setSelectAllCart(checked);
    const newSelected = {};
    if (checked) {
      cart.forEach(item => {
        newSelected[item.cartId] = true;
      });
    }
    setSelectedCartItems(newSelected);
  };



  const totalWeight = cart.length > 0
    ? cart.reduce((sum, o) => {
      const w = parseFloat(o.weight) || 0;
      const qty = parseInt(o.quantity) || 1;
      return sum + w * qty;
    }, 0)
    : 0;

  const handleCheckout = () => {
    if (cart.length === 0) {
      alert("Cart is empty");
      return;
    }
    setShowCartModal(true);
  };

  const closeCartModal = () => {
    setShowCartModal(false);
  };

  const handleShipNow = async () => {
    if (!user?.username) {
      alert("You must be logged in to place an order.");
      return;
    }

    if (!parentOrderId.trim()) {
      alert("⚠️ Please enter a Parent Order ID before placing the order.");
      return;
    }

    try {
      for (const item of cart) {
        if (!item.product || !item.product.code || item.quantity <= 0) {
          alert("❌ Invalid product or quantity.");
          return;
        }

        const order = {
          productCode: item.product.code,
          quantity: item.quantity,
          person: user.username,
          address: item.address || "N/A",
          status: "pending",
          weight: item.weight || "",
          size: item.size || "",
          color: item.color || "",
          remark: item.remark || '-',
          subType: item.product.subType || '-',
          parentOrderId
        };
        await api.createOrder(order);
      }

      alert(`✅ Successfully placed orders for ${cart.length} products.`);

      setCart([]);
      setShowCartModal(false);
    } catch (err) {
      console.error("Order placement failed", err.message);
      alert(`❌ Order failed: ${err.message}`);
    }
  };

  const handleExportExcel = () => {
    if (cart.length === 0) {
      alert('Cart is empty');
      return;
    }
    const dataToExport = cart.map(({ product, quantity, weight, size }) => ({
      Code: product.code,
      Name: product.name,
      Quantity: quantity,
      Weight: weight || '',
      Size: size || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Cart Items');
    XLSX.writeFile(workbook, 'cart_items.xlsx');
  };

  const handleExportPDF = () => {
    if (!cart || cart.length === 0) return alert('Nothing to print');

    // Group cart items by product code
    const groupedCart = cart.reduce((acc, item) => {
      const code = item.product.code;
      if (!acc[code]) acc[code] = [];
      acc[code].push(item);
      return acc;
    }, {});

    // Build grouped table rows
    const tableRows = Object.keys(groupedCart).flatMap((code) => {
      const groupItems = groupedCart[code];
      const product = groupItems[0].product;
      const imageUrl = product.image
        ? `https://azadi.ae/backend${product.image}`
        : "";
      const uniqueColors = [...new Set(groupItems.map((i) => i.color || ""))].filter(Boolean);
      const colorList = uniqueColors.join("<br>");

      // First (header) row for each product group
      const firstRow = `
      <tr>
        <td rowspan="${groupItems.length}" style="text-align:center;">
          ${imageUrl
          ? `<img src="${imageUrl}" alt="${product.name}" style="max-width:80px;max-height:80px;object-fit:contain;border-radius:6px;background-color:#fff8e1;padding:4px;" />`
          : `<div style="width:60px;height:60px;display:flex;align-items:center;justify-content:center;background:#f9f9f9;border-radius:6px;color:#999;font-size:10px;">No Img</div>`
        }
        </td>
        <td rowspan="${groupItems.length}" style="text-align:center;font-weight:bold;">${code}</td>
        <td rowspan="${groupItems.length}" style="text-align:center;">${colorList || "-"}</td>
        <td>${groupItems[0].weight || ""}</td>
        <td>${groupItems[0].size || ""}</td>
        <td>${groupItems[0].remark || ""}</td>
      </tr>
    `;

      // Remaining rows for this product
      const restRows = groupItems.slice(1).map(
        (i) => `
        <tr>
          <td>${i.weight || ""}</td>
          <td>${i.size || ""}</td>
          <td>${i.remark || ""}</td>
        </tr>
      `
      );

      return [firstRow, ...restRows];
    }).join("");

    // Build printable HTML
    const html = `
    <html>
      <head>
        <title>Cart Report</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 30px;
            color: #000;
          }
          h2 {
            text-align: center;
            margin-bottom: 20px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            font-size: 14px;
          }
          th, td {
            border: 1px solid #444;
            padding: 8px 12px;
            text-align: center;
            vertical-align: middle;
          }
          th {
            background-color: #f2f2f2;
            font-weight: bold;
          }
          td img {
            max-width: 80px;
            max-height: 80px;
            object-fit: contain;
            border-radius: 8px;
          }
        </style>
      </head>
      <body>
        <h2>Cart Items</h2>
        <p>Total Order: ${cart.length} | Total Weight: ${totalWeight}</p>
        <table>
          <thead>
            <tr>
              <th>Image</th>
              <th>Code</th>
              <th>Color(s)</th>
              <th>Weight</th>
              <th>Size</th>
              <th>Remark</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </body>
    </html>
  `;

    const printWindow = window.open("", "", "width=900,height=700");
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };


  const btnSx = {
    bgcolor: buttonBg,
    color: darkText,
    fontWeight: "bold",
    "&:hover": {
      bgcolor: buttonHover,
      color: "#fff",
    },
  };

  const btnOutlinedSx = {
    color: buttonBg,
    borderColor: buttonBg,
    fontWeight: "bold",
    "&:hover": {
      bgcolor: buttonBg,
      color: darkText,
      borderColor: buttonHover,
    },
  };

  const defaultSizes = {
    Ring: ["4", "4.5", "5", "5.5", "6", "6.5", "7", "7.5", "8", "8.5", "9", "9.5", "10", "10.5", "11", "11.5", "12"],
    BANGLE: ["15", "16", "17", "18", "19", "20", "21", "22", "23", "24"],
    chain: ["40CM", "45CM", "50CM", "55CM", "60CM", "65CM", "70CM", "75CM", "80CM"],
    Necklace: ["40CM", "45CM", "50CM", "55CM", "60CM"],
    Bracelet: ["16CM", "17CM", "18CM", "19CM", "20CM", "21CM", "22CM"],
    "FULL SET": ["16CM", "17CM", "18CM", "19CM", "20CM", "21CM", "22CM", "40CM", "45CM", "50CM", "55CM", "60CM"],
  };


  return (
    <div
      className="p-6 min-h-screen"
      style={{ backgroundColor: bgColor, color: darkText, fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}
    >
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <h2 className="text-3xl font-extrabold tracking-wide">Product Types</h2>
        <div className="flex gap-2 items-center w-full max-w-md">
          <TextField
            label="Search by Code"
            size="small"
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearchByCode()}
            fullWidth
            sx={{
              "& label": { color: taupeColor },
              "& .MuiInputBase-root": { color: darkText },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: taupeColor },
                "&:hover fieldset": { borderColor: buttonBg },
              },
            }}
          />
          <Button variant="contained" sx={btnSx} onClick={handleSearchByCode}>
            Search
          </Button>
        </div>
        <div>
          <Button
            variant="contained"
            sx={btnSx}
            onClick={handleCheckout}
            disabled={cart.length === 0}
          >
            Checkout ({cart.reduce((acc, item) => acc + item.quantity, 0)})
          </Button>
        </div>
      </div>

      <div className="max-w-md mb-6">
        <Autocomplete
          options={productTypes}
          getOptionLabel={(option) => option.name}
          value={selectedType}
          onChange={(e, newValue) => {
            if (newValue) handleTypeSelect(newValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Product Type"
              variant="outlined"
              size="small"
              sx={{
                "& label": { color: taupeColor },
                "& .MuiInputBase-root": { color: darkText },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: taupeColor },
                  "&:hover fieldset": { borderColor: buttonBg },
                },
              }}
              fullWidth
              disableClearable
            />
          )}
        />
      </div>

      {selectedType && (
        <>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-semibold tracking-wide text-[#5A503F]">
              Products in "{selectedType.name}"
            </h3>
            {role === "admin" && (
              <Button variant="contained" startIcon={<Add />} onClick={() => openProductModal()} sx={btnSx}>
                Add Product
              </Button>
            )}
          </div>

          {selectedType && subTypes.length > 0 && (
            <div className="max-w-md mb-6">
              <Autocomplete
                options={subTypes}
                getOptionLabel={(option) => option}
                value={selectedSubType || null}
                onChange={(e, newValue) => {
                  if (newValue) handleSubTypeSelect(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Sub-Type"
                    variant="outlined"
                    size="small"
                    sx={{
                      "& label": { color: taupeColor },
                      "& .MuiInputBase-root": { color: darkText },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: taupeColor },
                        "&:hover fieldset": { borderColor: buttonBg },
                      },
                    }}
                    fullWidth
                    disableClearable
                  />
                )}
                fullWidth
                disableClearable
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((product) => (
              <div key={product.id} className="relative p-4 border rounded-lg shadow-lg" style={{ borderColor: taupeColor, backgroundColor: "#fff8e1" }}>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    fontSize: '0.7rem',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    textTransform: 'none'
                  }}
                  onClick={() => toggleUnitMode(product.id)}
                >
                  {unitMode[product.id] === 'pieces' ? 'Set Weight' : 'Set Pieces'}
                </Button>

                {/* Add "Select All Sizes" button BELOW this */}
                {selectedType?.name && defaultSizes[selectedType.name.trim()] && (
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 40,
                      right: 8,
                      fontSize: '0.75rem',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      textTransform: 'none',
                      display: 'block'
                    }}
                    onClick={() => {
                      const sizes = defaultSizes[selectedType.name.trim()];

                      const newQuantities = { ...cartQuantities };
                      const newWeights = { ...cartWeights };
                      const newSizes = { ...cartSizes };
                      const newColor = { ...cartColor };
                      const newRemark = { ...cartRemark };

                      newQuantities[product.id] = sizes.map(() => 1); // default quantity
                      newWeights[product.id] = sizes.map(() => '');
                      newSizes[product.id] = [...sizes];
                      newColor[product.id] = sizes.map(() => 'Yellow'); // default color
                      newRemark[product.id] = sizes.map(() => '-');

                      setCartQuantities(prev => ({ ...prev, ...newQuantities }));
                      setCartWeights(prev => ({ ...prev, ...newWeights }));
                      setCartSizes(prev => ({ ...prev, ...newSizes }));
                      setCartColor(prev => ({ ...prev, ...newColor }));
                      setCartRemark(prev => ({ ...prev, ...newRemark }));
                    }}
                  >
                    Select All Sizes
                  </Button>
                )}


                {product.image && (
                  <div className="w-32 h-32 mb-3 overflow-hidden rounded-lg shadow flex items-center justify-center bg-#[fff8e1]">
                    <img
                      src={`https://azadi.ae/backend${product.image}`}
                      alt={product.name}
                      className="max-w-full max-h-32 object-contain rounded-lg"
                    />
                  </div>
                )}
                <div className="mb-1 text-[#5A503F]">
                  <strong>Code:</strong> {product.code}
                </div>
                <div className="mb-1 text-[#5A503F]">
                  <strong>Name:</strong> {product.name}
                </div>
                <div className="mb-1 text-[#5A503F]">
                  <strong>weight:</strong> {product.weight}
                </div>

                {((cartQuantities[product.id] || []).length === 0 ? [0] : [...Array((cartQuantities[product.id] || []).length).keys()]).map((index) => (
                  <div key={index} className="mt-2 flex items-center gap-2 flex-wrap">
                    <TextField
                      label={unitMode[product.id] === 'pieces' ? 'Pieces' : 'Weight'}
                      size="small"
                      value={cartWeights[product.id]?.[index] ?? ''}
                      onChange={(e) => {
                        const newWeights = { ...cartWeights };
                        if (!newWeights[product.id]) newWeights[product.id] = [];
                        newWeights[product.id][index] = e.target.value;
                        setCartWeights(newWeights);
                      }}
                      sx={{ width: 80 }}
                    />
                    <TextField
                      label="Size"
                      size="small"
                      value={cartSizes[product.id]?.[index] ?? ''}
                      onChange={(e) => {
                        const newSizes = { ...cartSizes };
                        if (!newSizes[product.id]) newSizes[product.id] = [];
                        newSizes[product.id][index] = e.target.value;
                        setCartSizes(newSizes);
                      }}
                      sx={{ width: 80 }}
                    />
                    <TextField
                      label="Color"
                      size="small"
                      value={cartColor[product.id]?.[index] ?? 'Yellow'}
                      onChange={(e) => {
                        const newColor = { ...cartColor };
                        if (!newColor[product.id]) newColor[product.id] = [];
                        newColor[product.id][index] = e.target.value;
                        setCartColor(newColor);
                      }}
                      sx={{ width: 90 }}
                    />
                    <TextField
                      label="Remark"
                      size="small"
                      value={cartRemark[product.id]?.[index] ?? '-'}
                      onChange={(e) => {
                        const newRemark = { ...cartRemark };
                        if (!newRemark[product.id]) newRemark[product.id] = [];
                        newRemark[product.id][index] = e.target.value;
                        setCartRemark(newRemark);
                      }}
                      sx={{ width: 100 }}
                    />
                    {index === 0 && (cartQuantities[product.id]?.length || 0) === 0 && (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          ...btnSx,
                          fontWeight: "bold",
                          fontSize: "0.875rem",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() => handleAddVariation(product.id)}
                      >
                        +
                      </Button>
                    )}
                    {index > 0 && (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          ...btnSx,
                          fontWeight: "bold",
                          fontSize: "0.875rem",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() => handleRemoveVariation(product.id, index)}
                      >
                        -
                      </Button>
                    )}
                    {index === (cartQuantities[product.id]?.length || 0) - 1 && (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          ...btnSx,
                          fontWeight: "bold",
                          fontSize: "0.875rem",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() => handleAddVariation(product.id)}
                      >
                        +
                      </Button>
                    )}
                  </div>
                ))}

                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    ...btnSx,
                    fontWeight: "bold",
                    fontSize: "0.875rem",
                    whiteSpace: "nowrap",
                    mt: 2,
                  }}
                  onClick={() => handleAddToCart(product)}
                >
                  Add to Cart
                </Button>

                {role === "admin" && (
                  <div className="flex gap-3 mt-4 flex-wrap">
                    <Button
                      variant="outlined"
                      sx={btnOutlinedSx}
                      onClick={() => openProductModal(product)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      sx={{
                        borderColor: "#B33A3A",
                        color: "#B33A3A",
                        "&:hover": {
                          bgcolor: "#B33A3A",
                          color: "#fff",
                          borderColor: "#B33A3A",
                        },
                      }}
                      onClick={() => handleDeleteProduct(product.id)}
                    >
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <Dialog
        open={showProductModal}
        onClose={closeProductModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: bgColor,
            color: darkText,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", color: goldColor }}>{editingId ? "Edit Product" : "Add Product"}</DialogTitle>
        <DialogContent className="space-y-4">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setForm({ ...form, image: e.target.files[0] })}
            style={{ marginBottom: 12 }}
          />

          <TextField
            fullWidth
            label="Product Code"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="weight"
            value={form.weight}
            onChange={(e) => setForm({ ...form, weight: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Product Type"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Factory Name"
            value={form.subType}
            onChange={(e) => setForm({ ...form, subType: e.target.value })}
            sx={{ mb: 2 }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={closeProductModal}>Cancel</Button>
          <Button variant="contained" onClick={handleProductSubmit}>
            {editingId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showCartModal}
        onClose={closeCartModal}
        fullWidth
        maxWidth="md"  // narrower than lg
        PaperProps={{
          sx: {
            backgroundColor: bgColor,
            color: darkText,
            minWidth: 600, // optional: prevents it from getting too narrow
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", color: goldColor }}>
          Cart ({cart.reduce((acc, item) => acc + item.quantity, 0)} items)
        </DialogTitle>

        <TextField
          label="Parent Order ID"
          variant="outlined"
          fullWidth
          value={parentOrderId}
          onChange={(e) => setParentOrderId(e.target.value)}
          sx={{
            "& label": { color: taupeColor },
            "& .MuiInputBase-root": { color: darkText },
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: taupeColor },
              "&:hover fieldset": { borderColor: buttonBg },
            },
            mb: 2,
          }}
          placeholder="Enter a Parent Order ID (e.g., KAJ20251110-001)"
        />

        {cart.length > 0 && (
          <div className="flex items-center gap-2 mb-2 px-2">
            <input
              type="checkbox"
              checked={selectAllCart}
              onChange={(e) => handleSelectAllCart(e.target.checked)}
            />
            <label>Select All</label>
          </div>
        )}

        <DialogContent sx={{ maxHeight: "60vh", overflowY: "auto" }}>
          {cart.length === 0 ? (
            <div>Your cart is empty</div>
          ) : (
            <div className="space-y-2">
              {cart.map((item) => (
                <div
                  key={item.cartId}
                  className="flex flex-col md:flex-row justify-between items-center border-b pb-2"
                  style={{ borderColor: taupeColor }}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!selectedCartItems[item.cartId]}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedCartItems((prev) => {
                          const updated = { ...prev, [item.cartId]: checked };
                          const allSelected = cart.every((ci) => updated[ci.cartId]);
                          setSelectAllCart(allSelected);
                          return updated;
                        });
                      }}
                    />
                    {item.product.image && (
                      <div className="w-20 h-20 overflow-hidden rounded-lg shadow flex items-center justify-center bg-[#fff8e1]">
                        <img
                          src={`https://azadi.ae/backend${item.product.image}`}
                          alt={item.product.name}
                          className="max-w-full max-h-full object-contain rounded-lg"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col md:flex-row gap-2 flex-1 mt-2 md:mt-0">
                    <div>
                      <strong>{item.product.name}</strong> (Code: {item.product.code})
                    </div>

                    <TextField
                      label="Weight"
                      size="small"
                      value={item.weight || ""}
                      onChange={(e) =>
                        setCart((prevCart) =>
                          prevCart.map((c) =>
                            c.cartId === item.cartId ? { ...c, weight: e.target.value } : c
                          )
                        )
                      }
                      sx={{ width: 80 }}
                    />
                    <TextField
                      label="Size"
                      size="small"
                      value={item.size || ""}
                      onChange={(e) =>
                        setCart((prevCart) =>
                          prevCart.map((c) =>
                            c.cartId === item.cartId ? { ...c, size: e.target.value } : c
                          )
                        )
                      }
                      sx={{ width: 80 }}
                    />
                    <TextField
                      label="Color"
                      size="small"
                      value={item.color || ""}
                      onChange={(e) =>
                        setCart((prevCart) =>
                          prevCart.map((c) =>
                            c.cartId === item.cartId ? { ...c, color: e.target.value } : c
                          )
                        )
                      }
                      sx={{ width: 90 }}
                    />
                    <TextField
                      label="Remark"
                      size="small"
                      value={item.remark || ""}
                      onChange={(e) =>
                        setCart((prevCart) =>
                          prevCart.map((c) =>
                            c.cartId === item.cartId ? { ...c, remark: e.target.value } : c
                          )
                        )
                      }
                      sx={{ width: 100 }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={closeCartModal}>Cancel</Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleRemoveSelectedItems}
            disabled={Object.keys(selectedCartItems).length === 0}
          >
            Remove Selected
          </Button>
          <Button variant="outlined" onClick={handleExportExcel} disabled={cart.length === 0}>
            Export to Excel
          </Button>
          <Button variant="outlined" onClick={handleExportPDF} disabled={cart.length === 0}>
            Export to PDF
          </Button>
          <Button variant="contained" onClick={handleShipNow} disabled={cart.length === 0}>
            Ship Now
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, onConfirm: null, message: '' })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "80vh",
            overflowY: "auto",
            backgroundColor: "#F4F1EE",
            color: "#3C3B3F",
            border: "1px solid #A89F91",
            borderRadius: 3,
            p: 3,
          },
        }}
      >
        <DialogContent>
          <div className="whitespace-pre-line font-semibold text-lg">
            {confirmDialog.message.split('\n').map((line, index) => (
              <p key={index}>{line}</p>
            ))}
          </div>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-end" }}>
          <Button
            onClick={() => setConfirmDialog({ open: false, onConfirm: null, message: '' })}
            sx={{
              bgcolor: "#3C3B3F",
              color: "#F4F1EE",
              "&:hover": { bgcolor: "#A89F91", color: "#3C3B3F" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDialog.onConfirm}
            sx={{
              bgcolor: "#A89F91",
              color: "#3C3B3F",
              "&:hover": { bgcolor: "#3C3B3F", color: "#F4F1EE" },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default Products;